import React, { memo } from 'react';
import { CpuIcon, ServerIcon, ThermometerIcon, GpuIcon, DatabaseIcon, PlusIcon } from 'lucide-react';
import { mockGpuData } from '../utils/mockData';
const getUtilizationColor = (utilization: string) => {
  const value = parseInt(utilization);
  if (value > 80) return 'bg-red-500';
  if (value > 50) return 'bg-yellow-500';
  return 'bg-green-500';
};
const getTemperatureColor = (temperature: string) => {
  const value = parseInt(temperature);
  if (value > 80) return 'text-red-500';
  if (value > 60) return 'text-yellow-500';
  return 'text-green-500';
};
const GpuOverview = () => {
  return <div className="space-y-6" data-id="element-171">
      <div className="flex items-center justify-between" data-id="element-172">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-173">GPU Overview</h1>
        <div className="flex items-center space-x-3" data-id="element-174">
          <button className="bg-gray-200 text-gray-500 px-4 py-2 rounded-md flex items-center cursor-not-allowed opacity-70" disabled data-id="element-175">
            <PlusIcon size={16} className="mr-2" data-id="element-176" />
            <span className="font-medium" data-id="element-177">Add a Note</span>
          </button>
          <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-md flex items-center" data-id="element-178">
            <ServerIcon size={18} className="mr-2" data-id="element-179" />
            <span className="font-medium" data-id="element-180">1 Active Node</span>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-181">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center" data-id="element-182">
          <CpuIcon size={20} className="mr-2 text-gray-500" data-id="element-183" />
          <h2 className="text-lg font-medium text-gray-800" data-id="element-184">GPU Status</h2>
        </div>
        <div className="overflow-x-auto" data-id="element-185">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-186">
            <thead className="bg-gray-50" data-id="element-187">
              <tr data-id="element-188">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-189">
                  Node (IP)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-190">
                  GPU Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-191">
                  Utilization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-192">
                  Memory Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-193">
                  Temp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-194">
                  Model(s) Running
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-195">
              <tr data-id="element-196">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-197">
                  {mockGpuData[0].nodeIp}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-198">
                  {mockGpuData[0].gpuType}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-199">
                  <div className="flex items-center" data-id="element-200">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2 max-w-[100px]" data-id="element-201">
                      <div className={`h-2.5 rounded-full ${getUtilizationColor(mockGpuData[0].utilization)}`} style={{
                      width: mockGpuData[0].utilization
                    }} data-id="element-202"></div>
                    </div>
                    <span className="text-sm text-gray-700" data-id="element-203">
                      {mockGpuData[0].utilization}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-204">
                  <div className="flex items-center" data-id="element-205">
                    <DatabaseIcon size={16} className="mr-2 text-gray-400" data-id="element-206" />
                    {mockGpuData[0].memoryUsage}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-207">
                  <div className="flex items-center" data-id="element-208">
                    <ThermometerIcon size={16} className={`mr-2 ${getTemperatureColor(mockGpuData[0].temperature)}`} data-id="element-209" />
                    <span className="text-sm text-gray-700" data-id="element-210">
                      {mockGpuData[0].temperature}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-211">
                  <span className={`px-2 py-1 text-xs rounded-full ${mockGpuData[0].model === 'Idle' ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-212">
                    {mockGpuData[0].model}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-213">
        <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-214">
          <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-215">
            GPU Utilization Summary
          </h2>
          <div className="space-y-4" data-id="element-216">
            <div data-id="element-217">
              <div className="flex justify-between mb-1" data-id="element-218">
                <span className="text-sm font-medium text-gray-700" data-id="element-219">
                  Total GPUs
                </span>
                <span className="text-sm text-gray-700" data-id="element-220">5</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-221">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                width: '100%'
              }} data-id="element-222"></div>
              </div>
            </div>
            <div data-id="element-223">
              <div className="flex justify-between mb-1" data-id="element-224">
                <span className="text-sm font-medium text-gray-700" data-id="element-225">
                  Active GPUs
                </span>
                <span className="text-sm text-gray-700" data-id="element-226">3 (60%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-227">
                <div className="bg-green-500 h-2.5 rounded-full" style={{
                width: '60%'
              }} data-id="element-228"></div>
              </div>
            </div>
            <div data-id="element-229">
              <div className="flex justify-between mb-1" data-id="element-230">
                <span className="text-sm font-medium text-gray-700" data-id="element-231">
                  High Utilization (&gt;80%)
                </span>
                <span className="text-sm text-gray-700" data-id="element-232">1 (20%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-233">
                <div className="bg-red-500 h-2.5 rounded-full" style={{
                width: '20%'
              }} data-id="element-234"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-235">
          <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-236">
            GPU Distribution
          </h2>
          <div className="space-y-4" data-id="element-237">
            <div className="flex items-center justify-between" data-id="element-238">
              <div className="flex items-center" data-id="element-239">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-240"></div>
                <span className="text-sm text-gray-700" data-id="element-241">NVIDIA A100</span>
              </div>
              <span className="text-sm font-medium" data-id="element-242">3 (60%)</span>
            </div>
            <div className="flex items-center justify-between" data-id="element-243">
              <div className="flex items-center" data-id="element-244">
                <div className="w-3 h-3 rounded-full bg-purple-500 mr-2" data-id="element-245"></div>
                <span className="text-sm text-gray-700" data-id="element-246">NVIDIA RTX 4090</span>
              </div>
              <span className="text-sm font-medium" data-id="element-247">1 (20%)</span>
            </div>
            <div className="flex items-center justify-between" data-id="element-248">
              <div className="flex items-center" data-id="element-249">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-250"></div>
                <span className="text-sm text-gray-700" data-id="element-251">NVIDIA RTX 3090</span>
              </div>
              <span className="text-sm font-medium" data-id="element-252">1 (20%)</span>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-253">
              <div className="flex items-center justify-between" data-id="element-254">
                <span className="text-sm font-medium text-gray-700" data-id="element-255">
                  Total Memory
                </span>
                <span className="text-sm font-medium" data-id="element-256">160 GB</span>
              </div>
              <div className="flex items-center justify-between mt-2" data-id="element-257">
                <span className="text-sm font-medium text-gray-700" data-id="element-258">
                  Used Memory
                </span>
                <span className="text-sm font-medium" data-id="element-259">48 GB (30%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default GpuOverview;